import React, { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

export type CloseReasonContextValue = {
	markKeyboardClose: () => void;
	consumeKeyboardClose: () => boolean;
};

// Default no-op used when a consumer renders outside the provider (e.g. isolated tests/Storybook).
const noop = () => undefined;
const returnFalse = () => false;

const GlobalTransitionsCloseReasonContext = createContext<CloseReasonContextValue>({
	markKeyboardClose: noop,
	consumeKeyboardClose: returnFalse,
});

// Scopes the "menu closed via keyboard" signal to a single diagram so focus return runs only for
// keyboard (Tab) closes, never pointer closes, without a module-level global or prop drilling.
export const GlobalTransitionsCloseReasonProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const closedByKeyboardRef = useRef(false);

	const value = useMemo(
		() => ({
			markKeyboardClose: () => {
				closedByKeyboardRef.current = true;
			},
			// Reads and clears so each keyboard close returns focus exactly once.
			consumeKeyboardClose: () => {
				const result = closedByKeyboardRef.current;
				closedByKeyboardRef.current = false;
				return result;
			},
		}),
		[],
	);

	return (
		<GlobalTransitionsCloseReasonContext.Provider value={value}>
			{children}
		</GlobalTransitionsCloseReasonContext.Provider>
	);
};

export const useGlobalTransitionsCloseReason = (): CloseReasonContextValue =>
	useContext(GlobalTransitionsCloseReasonContext);
